import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateHydrationMutation,
  useGetHydrationQuery,
  useUpdateHydrationMutation,
} from "../../../redux/Apis/Calculator/Hydration";
import { useStaticData } from "../../../utils/data";
import { setMetaTags } from "../../../utils/helper";
import { useTranslate } from "../../../context/TranslationContext";

const Hydration = () => {
  const { t } = useTranslate("hydration");
  const lang = useSelector((state) => state.language.lang);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authSlice);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [activityLevel, setActivityLevel] = useState("1.375");
  const [climate, setClimate] = useState("temperate");
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [createHydration] = useCreateHydrationMutation();
  const [updateHydration] = useUpdateHydrationMutation();
  const {
    data: hydrationHistoryData,
    refetch: refetchHydration,
    isLoading,
  } = useGetHydrationQuery({ lang }, { skip: !user });
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;

  const {
    data: hydData,
    refetch: refetchHydData,
    isLoading: loadingHyd,
  } = useGetHydrationQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user }
  );

  useEffect(() => {
    if (user) {
      refetchHydration();
    }
  }, [user, refetchHydration]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchHydData();
    }
  }, [startDate, endDate, user, refetchHydData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Hydration Calculator",
      "Estimate your daily water intake based on weight, activity, and climate to stay hydrated and perform at your best."
    );
  }, []);

  useEffect(() => {
    if (hydrationHistoryData) {
      setHistoryData(hydrationHistoryData.data || []);
    }
  }, [hydrationHistoryData]);

  const calculateHydration = () => {
    if (!weight || isNaN(weight) || weight <= 0) {
      toast.error(t("message.invalidWeight"));
      return;
    }

    const weightKg =
      weightUnit === "lbs" ? parseFloat(weight) / 2.205 : parseFloat(weight);
    let waterLiters = weightKg * 0.033;

    const activity = parseFloat(activityLevel);
    if (activity === 1.55) waterLiters += 0.35;
    if (activity >= 1.7) waterLiters += 0.7;

    if (climate === "hot") waterLiters += 0.5;
    if (climate === "very-hot") waterLiters += 0.7;

    const liters = waterLiters.toFixed(2);
    const cups = Math.round(waterLiters / 0.25);

    const output = {
      liters,
      cups,
      type: "Hydration",
      result: `${liters} L (${cups} cups)`,
    };

    setResult(output);

    if (
      location.state?.sectionIndex !== undefined &&
      location.state?.lectureIndex !== undefined
    ) {
      const storedResults =
        JSON.parse(localStorage.getItem("fitnessResults")) || {};
      storedResults[
        `${location.state.sectionIndex}-${location.state.lectureIndex}`
      ] = output;
      localStorage.setItem("fitnessResults", JSON.stringify(storedResults));
    }
    toast.success(t("message.saveSuccess"));
    return output;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("result.notYetCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result}</p>
      </div>
    );
  };

  const handleSaveResult = async (type, result) => {
    if (!result || !result.liters) {
      toast.error(t("message.saveError"));
      return;
    }

    if (!user) {
      toast.error(t("message.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        weight: `${weight} ${weightUnit}`,
        activity_level: activityLevel,
        climate: climate,
        result: `${result.liters} L (${result.cups} cups)`,
      };
      if (location?.state?.course) payload.course = location.state.course;
      const response = await createHydration({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("message.dataSaved"));
      refetchHydration();
      refetchHydData();
    } catch (error) {
      console.error("Error saving Hydration:", error);
      toast.error(t("message.saveFailed"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        const response = await updateHydration(payload).unwrap();
        toast.success(t("message.deleteSuccess"));
        refetchHydration();
        refetchHydData();
      }
    } catch (error) {
      console.error("Error deleting last Hydration:", error);
      toast.error(t("message.deleteError"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingHyd) {
    <Loader loading={true} />;
  }

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">{t("title")}</h4>
                  <div className="row">
                    <div className="col-md-8">
                      <Input
                        label={t("form.weightLabel")}
                        placeholder={t("form.weightPlaceholder")}
                        required
                        type="number"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <Input
                        label={t("form.unitLabel")}
                        type="select"
                        options={CALCULATOR_DATA.WEIGHT_UNITS}
                        value={weightUnit}
                        onChange={(e) => setWeightUnit(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <Input
                        label={t("form.activityLabel")}
                        type="select"
                        options={CALCULATOR_DATA.ACTIVITY_LEVELS}
                        value={activityLevel}
                        onChange={(e) => setActivityLevel(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("form.climateLabel")}
                        type="select"
                        options={CALCULATOR_DATA.CLIMATE_OPTIONS}
                        value={climate}
                        onChange={(e) => setClimate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-5">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text m-0">
                        {t("form.calculatePrompt")}
                      </p>
                      <Button
                        text={t("form.calculateBtn")}
                        onClick={calculateHydration}
                      />
                    </div>
                  </div>
                  {result && location.state?.returnTo && (
                    <div className="row mt-3">
                      <div className="col-12 text-center">
                        <Button
                          text={t("cta.continueLearning")}
                          // onClick={() => navigate(location.state.returnTo)}
                          onClick={() =>
                            navigate(location.state.returnTo, {
                              state: {
                                ...location.state,
                                resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-Hydration`,
                                resultData: {
                                  type: "Hydration",
                                  result: result,
                                  value: `Hydration: ${result}`,
                                  timestamp: new Date().toISOString(),
                                },
                              },
                            })
                          }
                          className="site-btn border-btn"
                          style={{ width: "100%", maxWidth: "200px" }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    {result ? (
                      <>
                        <h6>
                          {t("result.title")} {result.liters} liters (about{" "}
                          {result.cups} cups).
                        </h6>
                        <p className="p-sm">{t("result.description")}</p>
                      </>
                    ) : (
                      <h6>{t("result.displayText")}</h6>
                    )}
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">{t("cta.title")}</p>
                      <p className="m-0 grey-text p-sm">{t("cta.subtitle")}</p>
                      <Button text={t("cta.button")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FitnessToolsResultCard
              type="Hydration"
              result={
                result
                  ? {
                      ...result,
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={hydData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname
        )}
      />
      <Footer />
    </>
  );
};

export default Hydration;
